import { isClient, isPartner, SessionStorage } from '@witkac/kartamiejska-shared';
import Vue from 'vue';
import Router, { RouterOptions } from 'vue-router';

import AppLayout from '@/AppLayout.vue';
import Configuration from '@/components/configuration/Configuration.vue';
import store from '@/store/store';
import Confirm from '@/views/confirm/Confirm.vue';
import Dashboard from '@/views/dashboard/Dashboard.vue';
import Login from '@/views/login/Login.vue';
import LoginMfa from '@/views/login/LoginMfa.vue';
import PageNotFound from '@/views/PageNotFound.vue';
import PlaceSelect from '@/views/partners/PlaceSelect.vue';
import Profile from '@/views/profile/Profile.vue';
import Reset from '@/views/reset/Reset.vue';
import ResetPassword from '@/views/reset/ResetPassword.vue';
import termsCurrent from '@/views/terms/CurrentList.vue';
import { applicationRoute } from './routes/application.routes';
import { benefitRoute } from './routes/benefit.routes';
import { benefitUseListRoute } from './routes/benefitUsage.routes';
import { cardRoute } from './routes/card.routes';
import { categoryRoute } from './routes/category.routes';
import { clientDetailsRoute, clientListRoute } from './routes/client.routes';
import { collectionPointRoute } from './routes/collectionPoint.routes';
import { employeeDetailsRoute, employeeListRoute, employeeRolesRoute } from './routes/employee.routes';
import { groupListRoute } from './routes/group.routes';
import { officialDetailsRoute, officialListRoute, officialRolesRoute } from './routes/official.routes';
import { pageListRoute, pagesBySlotRoute } from './routes/page.routes';
import {
  partnerDetailsRoute,
  partnerEmployeeRolesRoute,
  partnerEmployeeRoute,
  partnerEmployeesRoute,
  partnerListRoute
} from './routes/partner.routes';
import { rolePermissionsRoute, roleTreeRoute } from './routes/role.routes';
import { settingGroupRoute, settingListRoute } from './routes/setting.routes';
import { templateListRoute } from './routes/template.routes';
import { termsListRoute } from './routes/terms.routes';
import { redirectSignedInUser } from './utils/redirectSignedInUser';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
      beforeEnter: (to, from, next) => redirectSignedInUser(next)
    },
    {
      path: '/login-mfa',
      name: 'loginMfa',
      component: LoginMfa,
      beforeEnter: (to, from, next) => redirectSignedInUser(next)
    },
    {
      path: '/confirm/:token',
      name: 'confirm',
      component: Confirm,
      beforeEnter: (to, from, next) => redirectSignedInUser(next)
    },
    {
      path: '/resend',
      name: 'resend',
      component: Reset,
      beforeEnter: (to, from, next) => redirectSignedInUser(next),
      props: {
        title: 'Wyślij ponownie link aktywacyjny',
        text: 'Wpisz adres e-mail, na który ma być wysłana wiadomość z linkiem do aktywacji konta:',
        method: 'sendConfirm'
      }
    },
    {
      path: '/reset',
      name: 'reset',
      component: Reset,
      beforeEnter: (to, from, next) => redirectSignedInUser(next)
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: ResetPassword,
      beforeEnter: (to, from, next) => redirectSignedInUser(next)
    },
    {
      path: '/place',
      name: 'placeSelect',
      component: PlaceSelect,
      meta: {
        toolbarTitle: 'Wybór obiektu',
        authorized: true
      }
    },
    {
      path: '/termsCurrent',
      name: 'termsCurrent',
      component: termsCurrent,
      beforeEnter: (to, from, next) => {
        Promise.all([
          store.dispatch('consents/list', { count: 0, userId: localStorage.getItem(SessionStorage.USER_ID) }),
          store.dispatch('terms/termsCurrent', {
            profileType: localStorage.getItem(SessionStorage.PROFILE_TYPE),
            required: true
          })
        ])
          .then(() => next())
          .catch(() => () => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy regulaminów' }));
      },
      meta: {
        toolbarTitle: 'Lista regulaminów do zaakceptowania'
      }
    },
    {
      path: '/',
      component: AppLayout,
      meta: {
        authorized: true
      },
      beforeEnter: (to, from, next) => {
        store.dispatch('auth/updateTokenOnExpire');
        next();
      },
      children: [
        {
          path: '',
          redirect: { name: 'dashboard' }
        },
        applicationRoute,
        benefitRoute,
        cardRoute,
        categoryRoute,
        clientListRoute,
        clientDetailsRoute,
        collectionPointRoute,
        partnerListRoute,
        partnerDetailsRoute,
        partnerEmployeesRoute,
        partnerEmployeeRoute,
        partnerEmployeeRolesRoute,
        employeeListRoute,
        employeeDetailsRoute,
        employeeRolesRoute,
        groupListRoute,
        templateListRoute,
        officialListRoute,
        officialDetailsRoute,
        officialRolesRoute,
        pageListRoute,
        pagesBySlotRoute,
        roleTreeRoute,
        rolePermissionsRoute,
        settingListRoute,
        settingGroupRoute,
        termsListRoute,
        benefitUseListRoute,
        {
          path: 'config',
          name: 'config',
          component: Configuration,
          beforeEnter: (to, from, next) => {
            store.dispatch('access/check', ['CONFIG.GET']).then(() => {
              if (!(store.state as any).access.checked['CONFIG.GET']) {
                store.commit('auth/unauthorize');
                next({ path: '/login', params: { redirectURL: to.fullPath } });
              }
              store.commit('breadcrumbs/set', [{ text: 'Konfiguracja', disabled: true }]);
              next();
            });
          }
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: Dashboard,
          beforeEnter: (to, from, next) => {
            if (isPartner()) {
              if (!localStorage.getItem(SessionStorage.PLACE_ID)) {
                next('/place');
              }
              store.commit('breadcrumbs/set', [{ text: 'Podsumowanie', disabled: true }]);

              const partnerId = parseInt(localStorage.getItem(SessionStorage.PARTNER_ID) as string);

              Promise.all([
                store.dispatch('stats/getCardUsages', partnerId),
                store.dispatch('stats/getBenefitsValue', partnerId)
              ])
                .then(() => next())
                .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
            } else {
              store.commit('breadcrumbs/set', [{ text: 'Panel administratora', disabled: true }]);

              Promise.all([
                store.dispatch('partners/list'),
                store.dispatch('stats/getCardUsages', undefined),
                store.dispatch('stats/getBenefitsValue', undefined),
                store.dispatch('stats/getPartnersCreated'),
                store.dispatch('stats/getActivatedCards'),
                store.dispatch('stats/getUnconfirmedProfiles'),
                store.dispatch('stats/getReadyCards')
              ])
                .then(() => next())
                .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać statystyk' }));
            }
          },
          meta: {
            toolbarTitle: 'Podsumowanie'
          }
        },
        {
          path: 'profile',
          name: 'profile',
          component: Profile,
          beforeEnter: (to, from, next) => {
            store.commit('breadcrumbs/set', [{ text: 'Profil', disabled: true }]);

            const storeName = isClient() ? 'client' : isPartner() ? 'profiles' : 'officials';
            store.commit(`${storeName}/setId`, localStorage.getItem(SessionStorage.USER_ID));
            store
              .dispatch(`${storeName}/get`)
              .then(() => next())
              .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych profilu' }));
          },
          meta: {
            toolbarTitle: 'Twój profil'
          }
        }
      ]
    },
    { path: '*', component: PageNotFound, meta: { authorized: true } }
  ]
} as RouterOptions);

router.beforeEach((to: any, from: any, next: any) => {
  if (to.matched.some((record: any) => record.meta.authorized)) {
    if (localStorage.getItem(SessionStorage.TOKEN) === null) {
      store.commit('auth/unauthorize');
      next({ path: '/login', params: { redirectURL: to.fullPath } });
    } else if (isPartner()) {
      const partnerId = parseInt(localStorage.getItem(SessionStorage.PARTNER_ID) as string);
      store.commit('partners/setId', partnerId);
      Promise.all([store.dispatch('partners/get'), store.dispatch('partners/places/list', { partnerId })]).then(() =>
        next()
      );
    } else {
      // Sprawdzanie roli
      next();
    }
  } else {
    next();
  }
});

export default router;
